import {
  TableContainer, CardBody, Card, Button, useToast, Modal, ModalOverlay,
  ModalContent, ModalCloseButton, ModalBody, ModalHeader, ModalFooter, Box, FormControl,
  FormLabel, Input, Select, Grid, Tag, Checkbox, Text,
} from '@chakra-ui/react'
import {useNavigate} from "react-router-dom";
import {DataTable} from "../components/dataTable";
import {createColumnHelper} from "@tanstack/react-table";
import {useAppDataStore} from "../mobx/appDataStore";
import {useEffect, useMemo, useState} from "react";
import {DeleteIcon, EditIcon} from "@chakra-ui/icons";
import {useFormik} from "formik";

const PRODUCTO = 'chatgpt'
const columnHelper = createColumnHelper();

const ChatGpt = () => {
  const appDataStore = useAppDataStore()
  const {socket} = appDataStore
  const rol = appDataStore.user?.rol?.name?.toLowerCase()
  const isDistribuidor = rol === 'distribuidor'
  const isAdmin = rol === 'admin'
  const [distribuidores, setDistribuidores] = useState([])
  const [refreshDependencies, setRefreshDependencies] = useState(0)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const toast = useToast()
  const navigate = useNavigate()

  const createForm = useFormik({
    initialValues: {email: '', secret: '', cantidadPerfiles: 1, limites: [''], distribuidorId: ''},
    onSubmit: (values) => {
      const payload = {
        email: values.email, secret: values.secret,
        limites: values.limites.map(l => l || null),
        distribuidorId: values.distribuidorId || null,
      }
      socket.emit('mail/links/chatgpt/create', payload, (res) => {
        if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
        setIsCreateOpen(false)
        setRefreshDependencies(Math.random())
        return toast({title: 'Cuenta creada', description: `Se crearon ${res.mailLinks.length} perfil(es)`, status: 'success', duration: 9000, isClosable: true})
      })
    },
  })

  const editForm = useFormik({
    initialValues: {perfil: '', limiteDiario: '', activo: true, usuarioAcceso: '', pin: '', correoDestino: '', distribuidorId: ''},
    onSubmit: (values) => {
      const payload = {id: selectedRow.id, ...values, limiteDiario: values.limiteDiario || null, distribuidorId: values.distribuidorId || null}
      socket.emit('mail/links/edit', payload, (res) => {
        if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
        setIsEditOpen(false)
        setSelectedRow(null)
        return toast({title: 'Guardado', status: 'success', duration: 9000, isClosable: true})
      })
    },
  })

  useEffect(() => {
    document.title = 'Tienda Digital - Links ChatGPT'
  }, []);

  useEffect(() => {
    if (!isAdmin) return
    socket.emit('datasource', {model: 'User', records: 1000, currentPage: 1, searchAttributes: [], include: ['Rol']},
      (res) => setDistribuidores((res.data || []).filter(u => ['distribuidor', 'subadmin'].includes(u.Rol?.name))))
  }, []);

  useEffect(() => {
    socket.on('mail/links/updated', () => setRefreshDependencies(Math.random()))
    return () => socket.off('mail/links/updated')
  }, []);

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text)
    return toast({title: 'Copiado', description: text, status: 'success', duration: 9000, isClosable: true})
  }

  const deleteLink = (row) => {
    socket.emit('mail/links/delete', {id: row.id}, (res) => {
      if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
      setRefreshDependencies(Math.random())
    })
  }

  const regenerateLink = () => {
    socket.emit('mail/links/regenerate', {id: selectedRow.id}, (res) => {
      if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
      setSelectedRow(res.mailLink)
      setRefreshDependencies(Math.random())
      return toast({title: 'Link regenerado', description: 'El link anterior ya no funciona', status: 'success', duration: 9000, isClosable: true})
    })
  }

  const deactivateLink = () => {
    socket.emit('mail/links/deactivate', {id: selectedRow.id}, (res) => {
      if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
      setSelectedRow(res.mailLink)
      editForm.setFieldValue('activo', false)
      setRefreshDependencies(Math.random())
      return toast({title: 'Link inactivado', status: 'success', duration: 9000, isClosable: true})
    })
  }

  const activateLink = () => {
    socket.emit('mail/links/activate', {id: selectedRow.id}, (res) => {
      if (res.result === 'error') return toast({title: res.error, status: 'error', duration: 9000, isClosable: true})
      setSelectedRow(res.mailLink)
      editForm.setFieldValue('activo', true)
      setRefreshDependencies(Math.random())
      return toast({title: 'Link activado', description: 'El cupo mensual queda disponible de nuevo', status: 'success', duration: 9000, isClosable: true})
    })
  }

  const onEdit = (row) => {
    setSelectedRow(row)
    editForm.setValues({
      perfil: row.perfil, limiteDiario: row.limiteDiario || '', activo: row.activo,
      usuarioAcceso: row.usuarioAcceso || '', pin: row.pin || '', correoDestino: row.correoDestino || '',
      distribuidorId: row.distribuidorId || '',
    })
    setIsEditOpen(true)
  }

  const onCreate = () => {
    createForm.resetForm()
    setIsCreateOpen(true)
  }

  // cantidad de perfiles determina cuántos campos de límite se piden — se preserva lo ya
  // escrito en los primeros N al subir/bajar la cantidad, en vez de limpiar todo el formulario.
  const onCantidadPerfilesChange = (e) => {
    const cantidad = Math.max(1, Number(e.target.value) || 1)
    const limites = Array.from({length: cantidad}, (_, i) => createForm.values.limites[i] || '')
    createForm.setFieldValue('cantidadPerfiles', cantidad)
    createForm.setFieldValue('limites', limites)
  }

  const onLimiteChange = (index, value) => {
    const limites = [...createForm.values.limites]
    limites[index] = value
    createForm.setFieldValue('limites', limites)
  }

  const dataFetch = async (records, currentPage, order, search, filter, attributes) => {
    return new Promise(resolve => {
      const args = {
        model: 'MailLink', records, currentPage, order, search, attributes, include: ['MailAccount'],
        filter: [...(filter || []), {key: 'producto', operator: 'eq', value: PRODUCTO}],
      }
      socket.emit('datasource', args, resolve)
    })
  };

  const columns = useMemo(() => ([
    columnHelper.accessor('id', {cell: (info) => info.getValue(), header: "ID"}),
    columnHelper.accessor(row => row.MailAccount?.email, {id: 'correo', cell: (info) => info.getValue(), header: "Correo"}),
    columnHelper.accessor('perfil', {cell: (info) => info.getValue(), header: "Perfil"}),
    columnHelper.accessor('limiteDiario', {cell: (info) => info.getValue() || 'Sin límite', header: "Límite mensual"}),
    columnHelper.accessor('distribuidorId', {
      enableColumnFilter: false,
      cell: (info) => {
        const id = info.getValue()
        const distribuidor = distribuidores.find(d => d.id === id)
        return distribuidor ? distribuidor.name : 'Admin'
      }, header: "Distribuidor"
    }),
    columnHelper.accessor('activo', {
      cell: (info) => {
        if (!info.getValue()) return <Tag colorScheme='red' variant='outline'>Inactivo</Tag>
        if (info.row.original.agotado) return <Tag colorScheme='orange' variant='outline'>Usado</Tag>
        return <Tag colorScheme='green' variant='outline'>Activo</Tag>
      },
      header: "Estado"
    }),
    columnHelper.accessor('linkId', {
      cell: (info) => {
        const url = `${window.location.origin}/correo/${info.getValue()}`
        return <Button colorScheme='green' size={'sm'} onClick={() => copyToClipboard(url)}>Copiar</Button>
      }, header: "Link"
    }),
    ...(isDistribuidor ? [] : [
      columnHelper.accessor('', {
        cell: (info) => <Button colorScheme='teal' variant={'outline'} size={'sm'} onClick={() => onEdit(info.row.original)}><EditIcon/></Button>,
        header: "Editar"
      }),
      columnHelper.accessor('', {
        cell: (info) => <Button color={'red'} colorScheme='teal' variant={'outline'} size={'sm'} onClick={() => deleteLink(info.row.original)}><DeleteIcon/></Button>,
        header: "Eliminar"
      }),
    ]),
  ]), [isDistribuidor, distribuidores])

  return (
    <Grid>
      <Card marginTop={5} marginLeft={5}>
        <CardBody>
          {!isDistribuidor && (
            <Button colorScheme='blue' onClick={onCreate} mb={5}>Nueva cuenta ChatGPT</Button>
          )}
          <Button marginLeft={2} colorScheme='blue' onClick={() => navigate('/admin/mailAccounts')} mb={5}>Cuentas vinculadas</Button>
          <Button marginLeft={2} colorScheme='blue' onClick={() => navigate('/admin/mailLinks')} mb={5}>Links Disney/Crunchyroll</Button>
          <Button marginLeft={2} colorScheme='blue' onClick={() => navigate('/admin/mailLinksNetflix')} mb={5}>Links Netflix</Button>
          <Button marginLeft={2} colorScheme='blue' onClick={() => navigate('/admin/mailHistorial')} mb={5}>Historial</Button>

          <TableContainer overflowX={'visible'} whiteSpace={'pre-wrap'} maxWidth={'1fr'}>
            <DataTable size={'sm'} columns={columns} data={[]} dataFetch={dataFetch} refreshDependencies={refreshDependencies}/>
          </TableContainer>
        </CardBody>

        <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)}>
          <ModalOverlay/>
          <ModalContent>
            <ModalHeader>Nueva cuenta ChatGPT</ModalHeader>
            <ModalCloseButton/>
            <ModalBody pb={6}>
              <FormControl>
                <FormLabel>Correo de la cuenta</FormLabel>
                <Input name={'email'} type={'email'} onChange={createForm.handleChange} value={createForm.values.email}/>
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Llave de verificación en dos pasos</FormLabel>
                <Input name={'secret'} onChange={createForm.handleChange} value={createForm.values.secret} placeholder={'ej. 4RNFGVX7PAGITWFLH72SU4C2PWQ2GO3S'}/>
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Cantidad de perfiles</FormLabel>
                <Input type={'number'} min={1} name={'cantidadPerfiles'} onChange={onCantidadPerfilesChange} value={createForm.values.cantidadPerfiles}/>
                <Box fontSize={'xs'} color={'gray.500'} mt={1}>Se crea un link independiente por cada perfil (Perfil 1, Perfil 2...).</Box>
              </FormControl>
              {createForm.values.limites.map((limite, i) => (
                <FormControl mt={4} key={i}>
                  <FormLabel>Límite mensual de consultas — Perfil {i + 1}</FormLabel>
                  <Input type={'number'} value={limite} onChange={(e) => onLimiteChange(i, e.target.value)}/>
                </FormControl>
              ))}
              {isAdmin && (
                <FormControl mt={4}>
                  <FormLabel>Distribuidor</FormLabel>
                  <Select name={'distribuidorId'} placeholder={'Sin asignar (admin)'} onChange={createForm.handleChange} value={createForm.values.distribuidorId}>
                    {distribuidores.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </Select>
                </FormControl>
              )}
            </ModalBody>
            <ModalFooter>
              <Button colorScheme='blue' onClick={createForm.submitForm}>Crear</Button>
              <Button onClick={() => setIsCreateOpen(false)}>Cancelar</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)}>
          <ModalOverlay/>
          <ModalContent>
            <ModalHeader>Editar perfil</ModalHeader>
            <ModalCloseButton/>
            <ModalBody pb={6}>
              <Text mb={3} color={'gray.500'}>Correo: <Text as={'b'}>{selectedRow?.MailAccount?.email}</Text></Text>
              <FormControl>
                <FormLabel>Perfil</FormLabel>
                <Input name={'perfil'} onChange={editForm.handleChange} value={editForm.values.perfil}/>
              </FormControl>
              <FormControl mt={4}>
                <FormLabel>Límite mensual de consultas</FormLabel>
                <Input type={'number'} name={'limiteDiario'} onChange={editForm.handleChange} value={editForm.values.limiteDiario}/>
              </FormControl>
              <FormControl mt={4}>
                <Checkbox name={'activo'} isChecked={editForm.values.activo} onChange={editForm.handleChange}>Activo</Checkbox>
              </FormControl>
              {selectedRow && (
                <FormControl mt={4}>
                  <FormLabel>Link</FormLabel>
                  <Input isReadOnly value={`${window.location.origin}/correo/${selectedRow.linkId}`}/>
                </FormControl>
              )}
            </ModalBody>
            <ModalFooter flexWrap={'wrap'} gap={2}>
              <Button colorScheme='orange' onClick={regenerateLink}>Regenerar link</Button>
              {editForm.values.activo
                ? <Button colorScheme='red' onClick={deactivateLink}>Inactivar link</Button>
                : <Button colorScheme='green' onClick={activateLink}>Activar link</Button>}
              <Button colorScheme='blue' onClick={editForm.submitForm}>Guardar</Button>
              <Button onClick={() => setIsEditOpen(false)}>Cancelar</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Card>
    </Grid>
  )
}

export default ChatGpt
